/**
 * SimulationContext - Contexto para modo simulación de roles
 *
 * Permite "simular" un rol para ver cómo se vería el sistema
 * con los permisos de ese rol, sin afectar la sesión real.
 *
 * @version 2.21.0
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { LegacyPermissions } from '@/types/permissions';
import type { Role } from '@/types';

interface DrawerState {
  role: Role | null;
  formData: {
    name: string;
    description: string;
    type: 'system' | 'custom';
  };
  permissions: LegacyPermissions;
}

/** Scope de sede/departamento del rol que se está simulando (mutuamente excluyentes) */
export interface SimulatedScope {
  sedeId: number | null;
  departmentId: number | null;
}

interface SimulationState {
  /** Si el modo simulación está activo */
  isSimulating: boolean;
  /** Nombre del rol que se está simulando */
  simulatedRoleName: string | null;
  /** Permisos del rol simulado */
  simulatedPermissions: LegacyPermissions | null;
  /** Scope (sede/departamento) del rol simulado — null si es global */
  simulatedScope: SimulatedScope | null;
  /** Timestamp de inicio de simulación */
  startedAt: Date | null;
  /** Estado guardado del drawer para restaurar al salir */
  savedDrawerState: DrawerState | null;
  /** Flag para indicar que se debe restaurar el drawer */
  pendingRestore: boolean;
}

interface SimulationContextType extends SimulationState {
  /** Iniciar simulación con permisos específicos */
  startSimulation: (
    roleName: string,
    permissions: LegacyPermissions,
    drawerState?: DrawerState,
    scope?: SimulatedScope | null
  ) => void;
  /** Detener simulación y volver a permisos reales */
  stopSimulation: () => void;
  /** Obtener el estado guardado del drawer y limpiar la flag de restore */
  consumeRestoreState: () => DrawerState | null;
  /** Verificar si un permiso está activo (considerando simulación) */
  hasSimulatedPermission: (module: string, action: string) => boolean;
  /** Obtener permisos efectivos (simulados o reales) */
  getEffectivePermissions: (realPermissions: LegacyPermissions) => LegacyPermissions;
  /** Obtener scope efectivo (sede/departamento simulados o reales) */
  getEffectiveScope: (realScope: SimulatedScope) => SimulatedScope;
}

const SimulationContext = createContext<SimulationContextType | null>(null);

export function SimulationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SimulationState>(() => {
    // Restaurar estado desde sessionStorage al iniciar
    if (typeof window !== 'undefined') {
      const isActive = sessionStorage.getItem('simulation_active') === 'true';
      if (isActive) {
        const roleName = sessionStorage.getItem('simulation_role');
        const permissionsJson = sessionStorage.getItem('simulation_permissions');
        const startedAtStr = sessionStorage.getItem('simulation_started_at');
        const drawerStateJson = sessionStorage.getItem('simulation_drawer_state');
        const scopeJson = sessionStorage.getItem('simulation_scope');

        if (roleName && permissionsJson) {
          try {
            const permissions = JSON.parse(permissionsJson);
            const drawerState = drawerStateJson ? JSON.parse(drawerStateJson) : null;
            const scope = scopeJson ? JSON.parse(scopeJson) : null;
            return {
              isSimulating: true,
              simulatedRoleName: roleName,
              simulatedPermissions: permissions,
              simulatedScope: scope,
              startedAt: startedAtStr ? new Date(startedAtStr) : new Date(),
              savedDrawerState: drawerState,
              pendingRestore: false,
            };
          } catch (e) {
            console.error('Error parsing simulation state:', e);
          }
        }
      }
    }
    return {
      isSimulating: false,
      simulatedRoleName: null,
      simulatedPermissions: null,
      simulatedScope: null,
      startedAt: null,
      savedDrawerState: null,
      pendingRestore: false,
    };
  });

  const startSimulation = useCallback((
    roleName: string,
    permissions: LegacyPermissions,
    drawerState?: DrawerState,
    scope?: SimulatedScope | null
  ) => {
    const startedAt = new Date();
    const effectiveScope = scope || null;
    setState({
      isSimulating: true,
      simulatedRoleName: roleName,
      simulatedPermissions: permissions,
      simulatedScope: effectiveScope,
      startedAt,
      savedDrawerState: drawerState || null,
      pendingRestore: false,
    });

    // Guardar en sessionStorage para persistir durante la sesión
    sessionStorage.setItem('simulation_active', 'true');
    sessionStorage.setItem('simulation_role', roleName);
    sessionStorage.setItem('simulation_permissions', JSON.stringify(permissions));
    sessionStorage.setItem('simulation_started_at', startedAt.toISOString());
    if (drawerState) {
      sessionStorage.setItem('simulation_drawer_state', JSON.stringify(drawerState));
    }
    if (effectiveScope) {
      sessionStorage.setItem('simulation_scope', JSON.stringify(effectiveScope));
    } else {
      sessionStorage.removeItem('simulation_scope');
    }
  }, []);

  const stopSimulation = useCallback(() => {
    // Mantener el estado del drawer pero marcar pendingRestore
    setState(prev => ({
      isSimulating: false,
      simulatedRoleName: null,
      simulatedPermissions: null,
      simulatedScope: null,
      startedAt: null,
      savedDrawerState: prev.savedDrawerState,
      pendingRestore: true,
    }));

    // Limpiar sessionStorage excepto el drawer state
    sessionStorage.removeItem('simulation_active');
    sessionStorage.removeItem('simulation_role');
    sessionStorage.removeItem('simulation_permissions');
    sessionStorage.removeItem('simulation_started_at');
    sessionStorage.removeItem('simulation_scope');
  }, []);

  const consumeRestoreState = useCallback((): DrawerState | null => {
    const savedState = state.savedDrawerState;

    if (state.pendingRestore && savedState) {
      setState(prev => ({
        ...prev,
        savedDrawerState: null,
        pendingRestore: false,
      }));
      sessionStorage.removeItem('simulation_drawer_state');
      return savedState;
    }

    return null;
  }, [state.savedDrawerState, state.pendingRestore]);

  const hasSimulatedPermission = useCallback((module: string, action: string): boolean => {
    if (!state.isSimulating || !state.simulatedPermissions) {
      return false;
    }
    return state.simulatedPermissions[module]?.[action] === true;
  }, [state.isSimulating, state.simulatedPermissions]);

  const getEffectivePermissions = useCallback((realPermissions: LegacyPermissions): LegacyPermissions => {
    if (state.isSimulating && state.simulatedPermissions) {
      return state.simulatedPermissions;
    }
    return realPermissions;
  }, [state.isSimulating, state.simulatedPermissions]);

  const getEffectiveScope = useCallback((realScope: SimulatedScope): SimulatedScope => {
    if (state.isSimulating && state.simulatedScope) {
      return state.simulatedScope;
    }
    return realScope;
  }, [state.isSimulating, state.simulatedScope]);

  const value = useMemo(() => ({
    ...state,
    startSimulation,
    stopSimulation,
    consumeRestoreState,
    hasSimulatedPermission,
    getEffectivePermissions,
    getEffectiveScope,
  }), [state, startSimulation, stopSimulation, consumeRestoreState, hasSimulatedPermission, getEffectivePermissions, getEffectiveScope]);

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation() {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return context;
}

export default SimulationContext;
